import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  Divider,
  useTheme,
  Button,
  Switch,
  FormControlLabel,
  InputAdornment,
  Skeleton,
  Autocomplete,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  useGetCardDetailMutation,
  useGetCardLimitsQuery,
  useUpdateCardLimitMutation,
  useRemoveCardLimitMutation,
  useGetCardLimitEffectTypesQuery,
} from '../../services/services';
import { encrypt } from '../../../../utils/encrypt';
import { ConfirmationModal } from '../../../../components/ConfirmationModal';
import { usePermission } from '../../../../hooks/usePermission';

const CardDetail = ({ cardDetail, handleClose }: any) => {
  // Redux store'dan companyId al
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);

  // Permission kontrolü
  const { hasPermission: canUpdateCardLimit } = usePermission({
    code: 'Corporate.POST.Updating.KartLimitGüncelleme',
  });
  const { hasPermission: canViewCardLimit } = usePermission({
    code: 'Corporate.GET.Reading.KartLimitGörüntüleme',
  });

  const theme = useTheme();
  const [getCardDetail] = useGetCardDetailMutation();
  const [cardDetailData, setCardDetailData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  // RTK Query ile kart limitlerini otomatik çek - customerId olarak companyId kullan
  const { data: cardLimitsData, isFetching: isCardLimitsFetching } = useGetCardLimitsQuery(
    {
      cardId: cardDetail?.id,
      customerId: currentUserState?.companyId?.toString() || ''
    },
    {
      skip: !cardDetail?.id || !currentUserState?.companyId || !canViewCardLimit
    }
  );

  // Kart limit tiplerini API'den çek
  const { data: cardLimitEffectTypesData, isFetching: isLimitTypesFetching } = useGetCardLimitEffectTypesQuery(
    undefined,
    {
      skip: !canViewCardLimit
    }
  );

  const cardLimits = cardLimitsData?.data?.data || cardLimitsData?.data;
  const hasFetchedRef = useRef<string | null>(null); // İstek kontrolü için

  // Limit alanları null mı kontrolü
  const isLimitsNull = cardLimits?.monthlyCardLimit === null &&
    cardLimits?.monthlyUsedLimit === null &&
    cardLimits?.monthlyRemainingLimit === null;

  // API'den gelen limit tiplerini dönüştür
  const limitTypes = cardLimitEffectTypesData?.data ? cardLimitEffectTypesData.data.map((item: any) => ({
    id: item.value.toString(),
    label: item.text,
    code: item.code,
    value: item.value
  })) : [];

  // Tablo verileri (Tanımlanmış ve Kalan kolonları) - API'den geliyor
  const [limitData, setLimitData] = useState<any>({});

  const [selectedLimitTypes, setSelectedLimitTypes] = useState<string[]>([]);

  const [newMonthlyLimit, setNewMonthlyLimit] = useState<number | null>(null);

  const [newLimit, setNewLimit] = useState<number | null>(null);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [updateCardLimit] = useUpdateCardLimitMutation();
  const [removeCardLimit] = useRemoveCardLimitMutation();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isUpdatingLimit, setIsUpdatingLimit] = useState(false);

  const [removingEffectType, setRemovingEffectType] = useState<string | null>(null);
  // Formatter fonksiyonları
  const limitFormatter = (value: any) => {
    const num = Number(value);
    return num ? `${num.toLocaleString('tr-TR')} TL` : '';
  };

  // Ortak TextField stili
  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: "rgba(241, 243, 244, 1) !important",
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
  };

  // Limit alanları için özel stiller
  const getLimitFieldStyle = (type: 'defined' | 'used' | 'remaining') => {
    const colors = {
      defined: theme.palette.success,
      used: theme.palette.warning,
      remaining: theme.palette.info,
    };

    return {
      ...textFieldStyle,
      '& .MuiOutlinedInput-root': {
        backgroundColor: colors[type].main + '08',
        '& fieldset': {
          borderColor: colors[type].main + '40',
        },
        '&:hover fieldset': {
          borderColor: colors[type].main,
        },
      },
      '& .MuiInputLabel-root': {
        color: colors[type].main,
        fontWeight: 600,
      },
      '& .MuiInputBase-input': {
        color: colors[type].dark,
        fontWeight: 600,
      },
    };
  };

  useEffect(() => {
    // Modal açıldığında sadece card detail'i çek (limits otomatik RTK Query ile çekilecek)
    if (!cardDetail?.id) {
      return;
    }

    if (isLoading) {
      return;
    }

    // Aynı cardId için tekrar istek atmasını önle
    const currentCardId = cardDetail.id;
    if (hasFetchedRef.current === currentCardId) {
      return;
    }

    const fetchData = async () => {
      hasFetchedRef.current = currentCardId;
      setIsLoading(true);

      try {
        // Sadece kart detayını çek, limits RTK Query ile otomatik çekilir
        const cardDetailResponse = await getCardDetail({ cardId: cardDetail.id });

        // Kart detaylarını set et
        if (cardDetailResponse?.data?.data) {
          setCardDetailData(cardDetailResponse.data.data);
        } else if (cardDetailResponse?.data) {
          setCardDetailData(cardDetailResponse.data);
        }
      } catch (error: any) {
        const errorMessage = error?.data?.message || error?.message || 'Bir hata oluştu';
        enqueueSnackbar(errorMessage, { variant: 'error' });
        hasFetchedRef.current = null; // Hata durumunda tekrar deneyebilsin
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [cardDetail?.id]);

  useEffect(() => {
    if (cardLimits && cardDetailData) {
      const isCardUnlimited =
        (cardDetailData?.limitAmountMonthly === 0 || cardDetailData?.limitAmountMonthly === null) &&
        isLimitsNull;

      setIsUnlimited(isCardUnlimited);
    }
  }, [cardLimits, cardDetailData, isLimitsNull]);

  useEffect(() => {
    if (!cardDetail?.id) {
      setNewLimit(null);
      setIsUnlimited(false);
      setCardDetailData(null);
      hasFetchedRef.current = null;
      // Yeni state'leri de sıfırla
      setSelectedLimitTypes([]);
      setNewMonthlyLimit(null);
    }
  }, [cardDetail?.id]);

  // API'den gelen limit tiplerini state'e aktar
  useEffect(() => {
    if (cardLimitEffectTypesData?.data && cardLimits) {

      const apiLimitData: any = {};

      // cardLimits direkt array olarak geliyor (swagger örneğindeki yapı)
      const limitsArray = Array.isArray(cardLimits) ? cardLimits : [];

      // API'den gelen her bir limit tipi için veri yapısı oluştur
      cardLimitEffectTypesData.data.forEach((item: any) => {
        const key = item.value.toString();

        // Bu limit tipine ait gerçek limit değerlerini bul
        // effectType ile code'u eşleştir
        const limitInfo = limitsArray.find((limit: any) => limit.effectType === item.code);


        const hasLimit = !!(limitInfo && limitInfo.effectType && limitInfo.effectType !== '');

        apiLimitData[key] = {
          defined: hasLimit ? limitInfo?.limit?.monthlyCardLimit ?? 0 : null,
          remaining: hasLimit ? limitInfo?.limit?.monthlyRemainingLimit ?? 0 : null,
          label: item.text,
          code: item.code,
          value: item.value,
          hasLimit,
        };
      });

      setLimitData(apiLimitData);
    }
  }, [cardLimitEffectTypesData, cardLimits]);

  // Limit tipi seçim işleyicisi
  const handleLimitTypeChange = (event: any, value: any[]) => {
    const hasAllInNew = value.some((item) => item.id === '0'); // '0' -> 'Hepsi'
    const hasAllInOld = selectedLimitTypes.includes('0');

    // "Hepsi" seçiliyorsa, tüm limit tiplerini seç
    if (hasAllInNew && !hasAllInOld) {
      const allTypes = limitTypes.map((type: any) => type.id);
      setSelectedLimitTypes(allTypes);
    }
    // "Hepsi" siliniyorsa, tüm seçimleri temizle
    else if (!hasAllInNew && hasAllInOld) {
      setSelectedLimitTypes([]);
    }
    // Normal durumlar
    else {
      const newIds = value.map((item) => item.id);
      // Eğer herhangi bir chip silindiyse ve "0" (Hepsi) hala seçiliyse, "0"ı da kaldır
      if (hasAllInNew && newIds.length < selectedLimitTypes.length) {
        setSelectedLimitTypes(newIds.filter(id => id !== '0'));
      } else {
        setSelectedLimitTypes(newIds);
      }
    }
  };

  // Yeni limiti güncelleme fonksiyonu
  const handleUpdateLimits = async () => {
    if (!newMonthlyLimit || newMonthlyLimit <= 0) {
      enqueueSnackbar('Lütfen geçerli bir limit değeri giriniz.', { variant: 'warning' });
      return;
    }

    if (selectedLimitTypes.length === 0) {
      enqueueSnackbar('Lütfen en az bir limit tipi seçiniz.', { variant: 'warning' });
      return;
    }

    if (!cardDetailData?.cardNo) {
      enqueueSnackbar('Kart numarası bulunamadı. Lütfen sayfa yenilenip tekrar deneyin.', { variant: 'error' });
      return;
    }

    try {
      const effectTypeCodes: string[] = [];
      if (selectedLimitTypes.includes('0')) {
        limitTypes.forEach((type: any) => {
          if (type.value !== 0) { effectTypeCodes.push(type.code); }
        });
      } else {
        selectedLimitTypes.forEach((typeId) => {
          const limitType = limitTypes.find((type: any) => type.id === typeId);
          if (limitType) { effectTypeCodes.push(limitType.code); }
        });
      }
      const updateResponse = await updateCardLimit({
        cardNo: encrypt(cardDetailData.cardNo),
        limitType: 'M',
        effectType: effectTypeCodes,
        limit: newMonthlyLimit,
      }).unwrap();
      
      if (updateResponse?.message) {
        enqueueSnackbar(updateResponse.message, { variant: 'success' });
      }
      // Form alanlarını temizle
      setSelectedLimitTypes([]);
      setNewMonthlyLimit(null);
    } catch (error: any) {
      console.error('❌ Limit güncellenirken hata:', error);
      const errorMessage = error?.data?.message || error?.message || 'Bir hata oluştu';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const changeLimitFnc = async () => {
    // CardNo validasyonu
    if (!cardDetailData?.cardNo) {
      enqueueSnackbar('Kart numarası bulunamadı. Lütfen sayfa yenilenip tekrar deneyin.', { variant: 'error' });
      return;
    }

    // Confirmation modal'ını aç
    setIsConfirmModalOpen(true);
  };

  const handleConfirmLimitChange = async () => { };
  const handleCloseConfirmModal = () => { };

  const handleRemoveLimit = async (effectCode: string) => {
    if (!cardDetailData?.cardNo) {
      enqueueSnackbar('Kart numarası bulunamadı. Lütfen sayfa yenilenip tekrar deneyin.', { variant: 'error' });
      return;
    }

    try {
      setRemovingEffectType(effectCode);

      const response = await removeCardLimit({
        cardNo: encrypt(cardDetailData.cardNo),
        effectType: effectCode,
      }).unwrap();

      if (response?.message) {
        enqueueSnackbar(response.message, { variant: 'success' });
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Bir hata oluştu';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setRemovingEffectType(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 0 }}>
      {/* Kart Detayları Bölümü */}
      <Card
        sx={{
          mb: 1,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              fontSize: '1.0625rem',
              mb: 2,
            }}
          >
            Kart Detayı
          </Typography>
          <Divider sx={{ my: 1, borderColor: '#1E3A8A' }} />

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              {isLoading || !cardDetailData ? (
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              ) : (
                <TextField
                  fullWidth
                  label="Kart Üzerindeki 1. İsim"
                  value={cardDetailData?.embossName || ''}
                  InputProps={{ readOnly: true }}
                  disabled
                  variant="outlined"
                  sx={textFieldStyle}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              {isLoading || !cardDetailData ? (
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              ) : (
                <TextField
                  fullWidth
                  label="Kart Üzerindeki 2. İsim"
                  value={cardDetailData?.embossName2 || ''}
                  InputProps={{ readOnly: true }}
                  disabled
                  variant="outlined"
                  sx={textFieldStyle}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              {isLoading || !cardDetailData ? (
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              ) : (
                <TextField
                  fullWidth
                  label="Kart Numarası"
                  value={cardDetailData?.cardNo || ''}
                  InputProps={{ readOnly: true }}
                  disabled
                  variant="outlined"
                  sx={textFieldStyle}
                />
              )}
            </Grid>

            {/* <Grid item xs={12} sm={6}>
              {isLoading || !cardDetailData ? (
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              ) : (
                <TextField
                  fullWidth
                  label="Limit Tipi"
                  value={cardDetailData?.cardType || ''}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  sx={textFieldStyle}
                />
              )}
            </Grid> */}
          </Grid>
        </CardContent>
      </Card>

      {/* Limit Bilgileri ve Limit Değiştir Bölümü - Tek Card İçinde */}
      {(canViewCardLimit || canUpdateCardLimit) && (
        <Card
          sx={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 2 }}>
            {/* Limit Bilgileri Tablosu */}
            {canViewCardLimit && (
              <>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    fontSize: '1.0625rem',
                    mb: 2,
                  }}
                >
                  Karta ait aylık limitler
                </Typography>
                <Divider sx={{ my: 1, borderColor: '#1E3A8A' }} />

                <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 'none', border: `1px solid ${theme.palette.divider}` }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                        <TableCell sx={{ fontWeight: 600, borderRight: `1px solid ${theme.palette.divider}` }}></TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, borderRight: `1px solid ${theme.palette.divider}` }}>
                          Tanımlanmış
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Kalan
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          İşlemler
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLimitTypesFetching ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <CircularProgress size={24} />
                          </TableCell>
                        </TableRow>
                      ) : Object.keys(limitData).length > 0 ? (
                        Object.keys(limitData)
                          .filter((key) => key !== '0') // 'Hepsi' seçeneğini tabloda gösterme
                          .map((key) => {
                            const row = limitData[key];
                            const hasLimit = row.hasLimit;

                            return (
                              <TableRow
                                key={key}
                                sx={{
                                  '&:nth-of-type(odd)': {
                                    backgroundColor: theme.palette.action.hover,
                                  },
                                }}
                              >
                                <TableCell sx={{ fontWeight: 500, borderRight: `1px solid ${theme.palette.divider}` }}>
                                  {row.label || key}
                                </TableCell>
                                <TableCell align="center" sx={{ borderRight: `1px solid ${theme.palette.divider}` }}>
                                  {hasLimit
                                    ? `${(row.defined ?? 0).toLocaleString('tr-TR')} TL`
                                    : '-'}
                                </TableCell>
                                <TableCell align="center" sx={{ borderRight: `1px solid ${theme.palette.divider}` }}>
                                  {hasLimit
                                    ? `${(row.remaining ?? 0).toLocaleString('tr-TR')} TL`
                                    : '-'}
                                </TableCell>
                                <TableCell align="center">
                                  {hasLimit && canUpdateCardLimit ? (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemoveLimit(row.code)}
                                      disabled={removingEffectType === row.code}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  ) : (
                                    ''
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>
                            Limit bilgisi bulunamadı
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Limit Değiştir Bölümü */}
            {canUpdateCardLimit && (
              <>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    fontSize: '1.0625rem',
                    mb: 2,
                    mt: canViewCardLimit ? 4 : 0,
                  }}
                >
                  Limit Değiştir
                </Typography>
                <Divider sx={{ my: 1, borderColor: '#1E3A8A' }} />

                <Grid container spacing={3} sx={{ mt: 1 }}>
                  {/* Limit Tipi Combobox */}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={limitTypes}
                      getOptionLabel={(option) => option.label}
                      value={limitTypes.find((type: any) => selectedLimitTypes[0] === type.id) || null}
                      onChange={(event, value) => {
                        setSelectedLimitTypes(value ? [value.id] : []);
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <li key={key} {...otherProps}>
                            {option.label}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Limit Tipi"
                          placeholder={selectedLimitTypes.length === 0 ? "Limit tipi seçiniz" : ""}
                          variant="outlined"
                          sx={textFieldStyle}
                        />
                      )}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.palette.background.paper,
                        },
                      }}
                    />
                  </Grid>

                  {/* Yeni Limit Input */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Yeni Limit (Aylık)"
                      type="number"
                      value={newMonthlyLimit || ''}
                      onChange={(e) => setNewMonthlyLimit(Number(e.target.value) || null)}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      variant="outlined"
                      placeholder="Yeni limit değerini giriniz"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">TL</InputAdornment>,
                      }}
                    // sx={{
                    //   ...textFieldStyle,
                    //   // Number input'un artırma/azaltma butonlarını gizle
                    //   '& input[type=number]': {
                    //     '-moz-appearance': 'textfield',
                    //   },
                    //   '& input[type=number]::-webkit-outer-spin-button': {
                    //     '-webkit-appearance': 'none',
                    //     margin: 0,
                    //   },
                    //   '& input[type=number]::-webkit-inner-spin-button': {
                    //     '-webkit-appearance': 'none',
                    //     margin: 0,
                    //   },
                    // }}
                    />
                  </Grid>

                  {/* Güncelle Butonu */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0 }}>
                      <Button
                        variant="contained"
                        onClick={handleUpdateLimits}
                        disabled={!newMonthlyLimit || newMonthlyLimit <= 0 || selectedLimitTypes.length === 0}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          fontSize: '0.875rem',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        }}
                      >
                        Güncelle
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Eski Confirmation Modal - Artık Kullanılmıyor */}
      {/* 
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmLimitChange}
        title="Limit Değiştir"
        content={`Limiti ${
          isUnlimited ? 'limitsiz' : `${newLimit?.toLocaleString('tr-TR')} TL`
        } olarak değiştirmek istediğinize emin misiniz?`}
        confirmButtonText="Evet, Değiştir"
        closeButtonText="Vazgeç"
        isLoading={isUpdatingLimit}
      />
      */}
    </Box>
  );
};

export default CardDetail;
